/**
 * useCallbacks — TanStack Query wrappers for the callbacks admin UI
 * (Story 7.13b). Wraps the typed `callbacksApi` client so the page/detail
 * components stay focused on rendering and don't repeat the
 * (queryKey, queryFn, invalidations) boilerplate at every call site.
 *
 * Query keys
 *   - ['callbacks', qs]   → list query, scoped by querystring
 *   - ['callback',  id]   → detail query, scoped by id
 *
 * Mutations invalidate BOTH ['callback', id] (current detail row) and the
 * full ['callbacks'] prefix so the list view picks up the new state on the
 * next mount/refetch.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { callbacksApi, type CallbackDetail, type CallbackListResponse } from '@/lib/callbacks';

export function useCallbacksList(qs: string) {
  return useQuery<CallbackListResponse>({
    queryKey: ['callbacks', qs],
    queryFn: () => callbacksApi.list(qs),
    placeholderData: keepPreviousData,
  });
}

export function useCallbackDetail(id: string | undefined) {
  return useQuery<CallbackDetail>({
    queryKey: ['callback', id],
    queryFn: () => callbacksApi.get(id as string),
    enabled: Boolean(id),
  });
}

export interface UseCallbackMutationOpts {
  onSuccess?: (data: CallbackDetail) => void;
  onError?: (err: unknown) => void;
}

export function useCancelCallback(id: string | undefined, opts: UseCallbackMutationOpts = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => callbacksApi.cancel(id as string, reason),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['callback', id] });
      qc.invalidateQueries({ queryKey: ['callbacks'] });
      opts.onSuccess?.(data);
    },
    onError: (err) => opts.onError?.(err),
  });
}

export function useRetryCallback(id: string | undefined, opts: UseCallbackMutationOpts = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => callbacksApi.retry(id as string),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['callback', id] });
      qc.invalidateQueries({ queryKey: ['callbacks'] });
      opts.onSuccess?.(data);
    },
    onError: (err) => opts.onError?.(err),
  });
}
